// Agent → operator interactions, aggregated across a conversation TREE
// (active chat + its subagent children) so a HITL prompt raised by a nested
// subagent surfaces in the parent Marcus chat (ENG-3976).
//
// Delivery is defense-in-depth, all reconciled through one merge-by-key
// reducer: an initial per-conversation HTTP fan-out on mount, a broad
// `#interaction#` prefix websocket subscription on BOTH partitions, AND a slow
// poll backstop. Two partitions are required because interaction rows
// broadcast on the partition of the conversation they belong to: the parent
// chat (and, post Part A, recorder children) live in the `{account}:{user}`
// partition while hunt / HITL-Romulus tenant children live in `{account}` —
// a single-partition subscription provably misses one side.
//
// Security gates (see security-plan.md):
//  - G2: reads are per-conversation gated (`/my?key=#interaction#{id}&convId=
//    {id}` resolves the conversation's partition server-side; visibility
//    within the account is NOT user-scoped — the account is the trust
//    boundary) — never a blanket tenant-wide interaction scan. The broad WS
//    prefix is bounded client-side by the conversation-id set for the same
//    reason.
//  - G5: every partition's events pass the same-account guard before merging.
//  - G6: if recording the answer fails after the ephemeral secret was created,
//    a best-effort compensating DELETE prevents an orphaned SSM blob.
//
// Answering a credential interaction is two POSTs: (1) stash the operator's
// inputs as an ephemeral broker secret, which returns a secret_ref; (2) record
// that secret_ref as the interaction's response — against the interaction's
// OWN conversation, so a child's prompt can be answered from the parent chat.
// The real values go to SSM via the broker, never into the (streamed,
// queryable) interaction row.
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { type WebSocketMessage } from '@/hooks/useWebSocket';
import { useAuth } from '@/state/auth';
import type { Interaction } from '@/types';

const POLL_INTERVAL_MS = 15_000;

// Per-BATCH parallelism bound for the fetch fan-out: a pathological subagent
// tree can't issue unbounded parallel /my requests, but EVERY conversation is
// still fetched — the fan-out runs in sequential chunks of this size rather
// than truncating the tree (ENG-4392; architecture plan §3.2).
export const MAX_FANOUT = 20;

// Broad prefix on both partitions instead of per-conversation patterns:
// per-child patterns risk MAX_SUBS_PER_REQUEST and churn on every child-list
// change. Scope is enforced client-side via the conversation-id set (G2).
const INTERACTION_PATTERNS = [
  { pattern: '#interaction#', matchType: 'prefix' as const },
];

function tenantAccount(tenant: string): string {
  const colon = tenant.indexOf(':');
  return colon === -1 ? tenant : tenant.slice(0, colon);
}

export function mergeInteraction(
  list: Interaction[],
  updated: Interaction
): Interaction[] {
  const idx = list.findIndex(i => i.key === updated.key);
  if (idx >= 0) {
    const local = list[idx];
    // Never regress a terminal local state back to pending: a delayed WS
    // INSERT/MODIFY echo (still `pending`) can arrive AFTER the operator's
    // optimistic `answered` flip; re-opening the prompt invites a resubmit and
    // a duplicate ephemeral broker secret. Keys are never reused (RequestID is
    // a fresh UUIDv7 per request — backend model.Interaction.Defaulted), so a
    // pending row for a terminal key is always a stale echo, never a new
    // request. Mirrors the identical guard in reconcileFetched so the WS path
    // and the fetch path encode the same terminal-state semantics.
    if (
      (local.status === 'answered' || local.status === 'expired') &&
      updated.status === 'pending'
    ) {
      return list;
    }
    return [...list.slice(0, idx), updated, ...list.slice(idx + 1)];
  }
  return [...list, updated];
}

/** Per-conversation fetch outcome: `rows === null` means the fetch FAILED. */
export type FetchResult = { id: string; rows: Interaction[] | null };

/**
 * Reconciles the locally-held interactions against a fetch fan-out result.
 * Each SUCCESSFUL per-conversation fetch is AUTHORITATIVE for its own
 * conversation (unlike the old blind merge-union): a locally-held row for that
 * conversation that is ABSENT from the snapshot is removed (covers a
 * server-side REMOVE/expiry the WS missed), and present rows are merged in.
 *
 * Three guards keep this from regressing state:
 *  - A FAILED fetch (`rows === null`) is best-effort — its conversation is left
 *    untouched, never emptied on a transient network error.
 *  - A terminal local state is never regressed to `pending`: if a local row is
 *    optimistically `answered` (or `expired`) and the fetched row for the SAME
 *    key is still `pending` (a slow fetch that raced the optimistic answer),
 *    the local terminal row wins so the prompt does not reopen.
 *  - Authoritative REMOVAL is scoped to `beforeKeys` — the set of interaction
 *    keys held at the moment the fetch was DISPATCHED. A locally-held row whose
 *    key is NOT in `beforeKeys` arrived (via WS or optimistic answer) AFTER the
 *    fetch was issued, so an empty/absent snapshot may simply pre-date it — it
 *    is KEPT this round and re-validated on the next poll. Without this a HITL
 *    prompt that lands mid-flight would be wiped for up to a poll interval.
 *  - `removedKeys` (keys REMOVEd by a WS event since the fetch was DISPATCHED)
 *    are never re-appended from the snapshot: the snapshot pre-dates the
 *    REMOVE, so re-adding would resurrect a deleted row for a poll interval.
 */
export function reconcileFetched(
  prev: Interaction[],
  results: FetchResult[],
  beforeKeys: Set<string>,
  removedKeys?: ReadonlySet<string>
): Interaction[] {
  const authoritative = new Map<string, Map<string, Interaction>>();
  for (const { id, rows } of results) {
    if (rows === null) continue; // failed fetch → non-authoritative
    const byKey = new Map<string, Interaction>();
    for (const row of rows) byKey.set(row.key, row);
    authoritative.set(id, byKey);
  }
  if (authoritative.size === 0) return prev; // nothing successful → no change

  const consumed = new Set<string>();
  const next: Interaction[] = [];

  for (const local of prev) {
    const auth = authoritative.get(local.conversationId);
    if (!auth) {
      // Conversation not fetched successfully → leave its rows untouched.
      next.push(local);
      continue;
    }
    const fetched = auth.get(local.key);
    if (!fetched) {
      // Absent from the authoritative snapshot. Only a genuine server-side
      // removal if the row existed when the fetch was dispatched; a row that
      // arrived mid-flight is exempt (kept, re-validated next poll).
      if (beforeKeys.has(local.key)) continue; // genuine removal → drop
      next.push(local); // arrived after dispatch → keep
      continue;
    }
    consumed.add(local.key);
    if (
      (local.status === 'answered' || local.status === 'expired') &&
      fetched.status === 'pending'
    ) {
      next.push(local); // never regress a terminal state back to pending
    } else {
      next.push(fetched);
    }
  }

  // Append rows present in an authoritative snapshot but not held locally yet
  // — EXCEPT rows whose key was REMOVEd after the fetch dispatched (the
  // snapshot pre-dates the REMOVE; re-appending would resurrect them).
  for (const byKey of authoritative.values()) {
    for (const [key, row] of byKey) {
      if (!consumed.has(key) && !removedKeys?.has(key)) next.push(row);
    }
  }

  return next;
}

/**
 * Answer an interaction in its OWN conversation — required so a subagent
 * child's prompt can be answered from the parent chat (pass
 * `interaction.conversationId`).
 */
export type SubmitCredential = (
  conversationId: string,
  requestId: string,
  inputs: Record<string, string>
) => Promise<void>;

export function useInteractions(
  activeConversationId?: string,
  conversationIds?: string[]
) {
  const axios = useAxios();
  const { isImpersonating, friend, me } = useAuth();
  const currentUser = isImpersonating ? friend : me;
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  // Mirror the latest interactions so fetchPending can snapshot the keys present
  // at DISPATCH time (before its await) — scoping authoritative removal so a WS
  // row that lands mid-flight isn't wiped by a snapshot that pre-dates it.
  const interactionsRef = useRef<Interaction[]>(interactions);
  interactionsRef.current = interactions;
  // Keys REMOVEd by a WS event while a fetch fan-out is in flight. The fetch
  // snapshot pre-dates those REMOVEs, so reconcileFetched must not re-append
  // them (zombie resurrection). Null whenever no fetch is in flight; only one
  // fetch is ever in flight at a time (fetchInFlightRef below).
  const removedDuringFetchRef = useRef<Set<string> | null>(null);

  // Effective tree scope: the caller-provided id set unioned with the active
  // conversation (so pendingForActive always has data), deduped. NOT capped —
  // the WS/display gate and the fetch fan-out both cover the FULL tree so a
  // prompt from any child surfaces regardless of tree size (ENG-4392). The
  // fan-out throttles via batching (see fetchPending), not truncation.
  const uniqueIds = [
    ...new Set(
      [activeConversationId, ...(conversationIds ?? [])].filter(
        (id): id is string => !!id
      )
    ),
  ];
  // Stable serialization for the fetch/subscription effects (mirrors
  // patternsKey in useRealtimeSubscription): an array-identity change with
  // identical contents must not refire the fan-out (React 19 Compiler is on,
  // no manual dep arrays — R7).
  const idsKey = JSON.stringify([...uniqueIds].sort());

  const handleEvent = useCallback(
    (ws: WebSocketMessage) => {
      if (!currentUser) return;
      // G5: same-account guard, identical on both partitions. Tenant-partition
      // events carry `{account}`, user-partition events `{account}:{user}` —
      // tenantAccount() reduces both to the account, which must be ours.
      // Fail closed on a missing tenant: every real event on either partition
      // carries one, so a tenant-less row is anomalous and must not slip past
      // the account check on the id-set gate alone.
      if (!ws.tenant || tenantAccount(ws.tenant) !== currentUser) return;
      if (ws.eventName === 'REMOVE') {
        // Tombstone the key while a fetch fan-out is in flight so the stale
        // snapshot cannot resurrect the removed row (see reconcileFetched).
        removedDuringFetchRef.current?.add(ws.key);
        setInteractions(prev => prev.filter(i => i.key !== ws.key));
        return;
      }
      const rows = (ws.item?.interactions ?? []) as Interaction[];
      if (rows.length === 0) return;
      // G2: the broad `#interaction#` prefix subscription is bounded to the
      // user's own conversation tree — drop anything out-of-tree so another
      // operator's prompt in a shared account never surfaces here.
      const idSet = new Set(JSON.parse(idsKey) as string[]);
      // Merge EVERY in-tree row (N-B): a WS item may carry more than one
      // interaction; taking only rows[0] would silently drop the rest.
      const inTree = rows.filter(
        row => row.conversationId && idSet.has(row.conversationId)
      );
      if (inTree.length === 0) return;
      setInteractions(prev => inTree.reduce(mergeInteraction, prev));
    },
    [currentUser, idsKey]
  );

  // Dual-partition realtime (§3.3): user partition for the parent chat's own
  // rows (and Part A recorder children), tenant partition for hunt /
  // HITL-Romulus tenant children. useWebSocket pools one connection per
  // partition key app-wide, so this adds at most one extra pooled connection.
  const subscriptionEnabled = uniqueIds.length > 0;
  useRealtimeSubscription({
    patterns: INTERACTION_PATTERNS,
    user: true,
    enabled: subscriptionEnabled,
    onUpdate: handleEvent,
  });
  useRealtimeSubscription({
    patterns: INTERACTION_PATTERNS,
    user: false,
    enabled: subscriptionEnabled,
    onUpdate: handleEvent,
  });

  // Guards against overlapping polls on a slow network: a tick that fires
  // while a previous fetchPending is still in flight is skipped (F-E). The
  // mount fetch and each non-overlapping tick still run.
  const fetchInFlightRef = useRef(false);

  // Fetch fan-out on mount + poll as a backstop for missed/flaky WS pushes.
  // Each SUCCESSFUL per-conversation fetch is authoritative for its own
  // conversation (reconcileFetched), so it removes rows the WS missed the
  // REMOVE for, while a failed fetch and terminal optimistic states are
  // preserved — see reconcileFetched.
  const fetchPending = useCallback(async () => {
    const fanoutIds = JSON.parse(idsKey) as string[];
    if (fanoutIds.length === 0) return;
    // Skip overlapping polls (F-E). A tick skipped here (incl. a just-added
    // conversation id whose mount fetch coincides with an in-flight run) is
    // backstopped by the `#interaction#` WS subscription and the next poll, so
    // at worst it costs one poll interval — never a missed row.
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    // Snapshot the keys held right now: only rows present at dispatch may be
    // authoritatively REMOVED below, so a WS row that arrives during the await
    // (a mid-flight HITL prompt) is exempt from removal this round.
    const beforeKeys = new Set(interactionsRef.current.map(i => i.key));
    // Collect keys the WS REMOVEs while this fetch is in flight: the snapshot
    // pre-dates them, so reconcileFetched must not re-append them. The local
    // set (not the ref) is closed over by the setInteractions updater below —
    // a REMOVE arriving after this frame is ordered after that updater and
    // filters the row itself.
    const removedDuringFetch = new Set<string>();
    removedDuringFetchRef.current = removedDuringFetch;
    try {
      // G2: one gated read per conversation — convId lets /my resolve that
      // conversation's partition AND visibility server-side (per-user vs tenant
      // for hunt/Public). NEVER a blanket `#interaction#` tenant scan. A read
      // that succeeds is authoritative for its conversation; one that throws
      // returns `rows: null` so its conversation is left untouched.
      //
      // EVERY conversation is fetched (ENG-4392); the fan-out just runs in
      // sequential chunks of MAX_FANOUT so a large tree can't issue unbounded
      // parallel requests. `beforeKeys` (dispatch-time) and authoritative
      // removal below therefore still span the full set fetched this cycle.
      const fetchOne = async (id: string): Promise<FetchResult> => {
        try {
          const resp = await axios.get('/my', {
            params: { key: `#interaction#${id}`, convId: id },
          });
          return {
            id,
            rows: (resp.data?.interactions ?? []) as Interaction[],
          };
        } catch {
          // Best-effort per-conversation backstop; WS is the primary path.
          return { id, rows: null };
        }
      };

      const results: FetchResult[] = [];
      for (let i = 0; i < fanoutIds.length; i += MAX_FANOUT) {
        const batch = fanoutIds.slice(i, i + MAX_FANOUT);
        results.push(...(await Promise.all(batch.map(fetchOne))));
      }
      setInteractions(prev =>
        reconcileFetched(prev, results, beforeKeys, removedDuringFetch)
      );
    } finally {
      removedDuringFetchRef.current = null;
      fetchInFlightRef.current = false;
    }
  }, [axios, idsKey]);

  useEffect(() => {
    if (idsKey === '[]') return;
    // setState happens after the await inside fetchPending (a later microtask),
    // not synchronously in this effect — same mount-fetch shape as useChatWorker.

    void fetchPending();
    const id = setInterval(() => void fetchPending(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [idsKey, fetchPending]);

  // Derivations, computed during render from the merged list + the id set.
  // Uncapped: a pending interaction from ANY child in the tree surfaces here
  // regardless of tree size (ENG-4392).
  const idSet = new Set(uniqueIds);
  // All pending interactions across the tree, oldest-first.
  //
  // Scoped to kind 'credential': that is the only kind this surface can
  // service (the drawer renders a credential form and answers with an SSM
  // secret_ref). The 'approval' kind — Response "true"/"false", no field UI —
  // has no producer today and no drawer affordance; surfacing it would
  // advertise a request whose only answer path submits the wrong payload.
  // Approval support (an Allow/Deny body) ships with its first emitter — see
  // ENG-4403. Until then, keep the roster honest.
  const pendingInteractions = interactions
    .filter(
      i =>
        i.status === 'pending' &&
        i.kind === 'credential' &&
        idSet.has(i.conversationId)
    )
    .sort((a, b) => (a.timestamp ?? '').localeCompare(b.timestamp ?? ''));
  // Oldest pending in the ACTIVE conversation — what to prompt inline.
  const pendingForActive = pendingInteractions.find(
    i => i.conversationId === activeConversationId
  );
  // Per-conversation oldest pending, for "needs input" indicators.
  const pendingByConversationId = new Map<string, Interaction>();
  for (const i of pendingInteractions) {
    if (!pendingByConversationId.has(i.conversationId)) {
      pendingByConversationId.set(i.conversationId, i);
    }
  }

  const submitCredential = useCallback<SubmitCredential>(
    async (conversationId, requestId, inputs): Promise<void> => {
      if (!conversationId) {
        throw new Error('cannot submit an interaction without a conversation');
      }
      if (!inputs || Object.keys(inputs).length === 0) {
        throw new Error('cannot submit an interaction without inputs');
      }
      // Ephemeral is a generic KV store — the params ARE the key/value payload.
      // Plaintext stops here (SSM via the broker); only the secret_ref flows on.
      const broker = await axios.post('/broker', {
        operation: 'add',
        type: 'ephemeral',
        category: 'env-integration',
        parameters: inputs,
      });
      const secretRef = broker.data?.credentialValue?.credential_id;
      if (!secretRef) {
        throw new Error('broker did not return a credential id');
      }
      try {
        await axios.post('/planner/interaction', {
          conversationId,
          requestId,
          response: secretRef,
        });
      } catch (err) {
        // G6: the answer never landed (e.g. the interaction expired under the
        // shortened await), so the ephemeral secret from POST 1 would be
        // orphaned in SSM. Best-effort compensating delete; the backend
        // sweeper (G1) is the backstop if this also fails.
        try {
          await axios.delete('/broker', {
            data: {
              credentialId: secretRef,
              type: 'ephemeral',
              category: 'env-integration',
            },
          });
        } catch {
          // Swallow: surface the original failure, not the cleanup's.
        }
        throw err;
      }
      // Optimistic: mark answered so the prompt closes before the WS echo.
      setInteractions(prev =>
        prev.map(i =>
          i.conversationId === conversationId && i.requestId === requestId
            ? { ...i, status: 'answered' }
            : i
        )
      );
    },
    [axios]
  );

  return {
    pendingForActive,
    pendingInteractions,
    pendingByConversationId,
    submitCredential,
  };
}
